import { action, computed, makeObservable } from 'mobx';
import { CasinoGameModelExt } from 'src/domains/casino/state/AppCasino/store/game-store/CasinoGameModelExt';
import {
    CollectionWithBingoType,
    ProviderType,
    SessionConfigType,
    SessionDataType,
    SessionResponseType,
} from 'src/domains/casino/types/types';
import { Resource } from 'src_common/common/mobx-utils/Resource';

export type GameDataType =
    | {
          type: 'bingo';
      }
    | {
          type: 'model';
          gameModel: CasinoGameModelExt;
          mode?: 'mini' | 'demo' | 'bingo-mini';
      };

export class GameDataState {
    public constructor(
        private readonly data: GameDataType,
        private config: SessionConfigType,
        public resource: Resource<SessionDataType> | null
    ) {
        makeObservable(this);
    }

    @computed public get id(): number {
        if (this.data.type === 'bingo') {
            return 0;
        }

        return this.data.gameModel.id;
    }

    @computed public get isDemo(): boolean {
        switch (this.data.type) {
            case 'bingo':
                return false;
            case 'model':
                return this.data.mode === 'demo';
        }
    }

    @computed public get isMini(): boolean {
        switch (this.data.type) {
            case 'bingo':
                return false;
            case 'model':
                return this.data.mode === 'mini' || this.data.mode === 'bingo-mini';
        }
    }

    @computed public get name(): string {
        if (this.data.type === 'bingo') {
            return 'Bingo';
        }
        return this.data.gameModel.name;
    }

    @computed public get gameType(): CollectionWithBingoType {
        if (this.data.type === 'bingo') {
            return 'bingo';
        }
        return this.data.gameModel.collection;
    }

    @computed public get provider(): ProviderType {
        if (this.data.type === 'bingo') {
            return 'pragmatic';
        }
        return this.config.provider;
    }

    @computed public get launchId(): string {
        if (this.data.type === 'bingo') {
            return '9999';
        }
        return this.data.gameModel.launchGameId;
    }

    @computed public get session(): SessionResponseType {
        const result = this.resource?.get();

        if (result?.type === 'loading') {
            return {
                status: 'loading',
            };
        }

        if (result?.type === 'ready') {
            return {
                status: 'ready',
                value: result.value,
            };
        }

        return {
            status: 'error',
            message: "Game session couldn't be launched. Try again later.",
        };
    }

    @computed public get mobile(): boolean {
        return this.config.params.mobile === true;
    }

    @action public clearResourceAndWait = async (): Promise<void> => {
        return await this.resource?.refresh();
    };
}
